import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, Loader2, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Verificar token ao carregar
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Token não fornecido');
        setVerifying(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/password-reset/verify?token=${token}`, {
          method: 'POST'
        });

        if (response.ok) {
          setTokenValid(true);
        } else {
          const data = await response.json();
          setError(data.detail || 'Token inválido');
        }
      } catch (err) {
        setError('Erro ao verificar token');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações
    if (!password.trim()) {
      toast.error('Digite a nova senha');
      return;
    }
    
    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          token: token,
          new_password: password 
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        toast.success('Senha redefinida com sucesso!');
        // Redirecionar para login após 3 segundos
        setTimeout(() => navigate('/login'), 3000);
      } else {
        toast.error(data.detail || 'Erro ao redefinir senha');
      }
    } catch (error) {
      toast.error('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="animate-spin text-yellow-500 mx-auto mb-4" size={48} />
          <p className="text-gray-400">Verificando link...</p>
        </div>
      </div>
    );
  }

  // Error state (token inválido)
  if (!tokenValid && !success) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="text-red-500" size={32} />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">Link Inválido</h2>
            
            <p className="text-gray-400 mb-6">
              {error || 'Este link de recuperação é inválido ou já foi utilizado.'}
            </p>
            
            <div className="space-y-3">
              <Link
                to="/forgot-password"
                className="block w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-black rounded-lg font-bold transition-colors text-center"
              >
                Solicitar Novo Link
              </Link>
              
              <Link
                to="/login"
                className="flex items-center justify-center gap-2 w-full py-3 border border-gray-700 hover:border-gray-600 text-gray-300 hover:text-white rounded-lg transition-colors"
              >
                <ArrowLeft size={20} />
                Voltar ao Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-green-500" size={32} />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">Senha Redefinida!</h2>
            
            <p className="text-gray-400 mb-6">
              Sua senha foi alterada com sucesso. Você será redirecionado para o login em instantes...
            </p>
            
            <Link
              to="/login"
              className="block w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-black rounded-lg font-bold transition-colors text-center"
            >
              Ir para Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-yellow-500 mb-2">🔧 Gestão de Máquinas</h1>
          <p className="text-gray-400">Redefinir Senha</p>
        </div>

        {/* Form Card */}
        <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-white mb-2">Nova Senha</h2>
            <p className="text-gray-400 text-sm">
              Digite sua nova senha abaixo. Use pelo menos 6 caracteres.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nova Senha */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Nova Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-12 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:border-yellow-500 focus:outline-none transition-colors"
                  data-testid="reset-password-new"
                  disabled={loading}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400 transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Confirmar Senha */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Confirmar Nova Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-12 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:border-yellow-500 focus:outline-none transition-colors"
                  data-testid="reset-password-confirm"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {password && confirmPassword && password !== confirmPassword && (
                <p className="text-red-400 text-sm mt-2">As senhas não coincidem</p>
              )}
            </div>

            {/* Password requirements */}
            <div className="bg-gray-900/50 rounded-lg p-4">
              <p className="text-gray-400 text-sm mb-2">Requisitos da senha:</p>
              <ul className="space-y-1 text-sm">
                <li className={`flex items-center gap-2 ${password.length >= 6 ? 'text-green-400' : 'text-gray-500'}`}>
                  {password.length >= 6 ? <CheckCircle size={14} /> : <div className="w-3.5 h-3.5 rounded-full border border-gray-600" />}
                  Pelo menos 6 caracteres
                </li>
                <li className={`flex items-center gap-2 ${password && confirmPassword && password === confirmPassword ? 'text-green-400' : 'text-gray-500'}`}>
                  {password && confirmPassword && password === confirmPassword ? <CheckCircle size={14} /> : <div className="w-3.5 h-3.5 rounded-full border border-gray-600" />}
                  Senhas coincidem
                </li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading || password.length < 6 || password !== confirmPassword}
              className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-500/50 disabled:cursor-not-allowed text-black font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="reset-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Redefinindo...
                </>
              ) : (
                <>
                  <Lock size={20} />
                  Redefinir Senha
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-600 text-sm mt-6">
          © 2024 Gestão de Máquinas - gestaodemaquinas.com.br
        </p>
      </div>
    </div>
  );
}
